import i18n from "i18next";
import { useCallback } from "react";
import { useDispatch } from "react-redux";

import type { FormSubmitAction } from "../../../../components/Form/types/FormProps";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { showErrorNotification } from "../../../../core/setup/redux/state/notifications/notificationsState";
import { useCampaignMutation, useCampaignSaveMutation } from "../services/server/CampaignApi";
import type { CampaignEntity } from "../types/CampaignEntity";

// Saves/updates a Campaign via the given RTK Query mutation hook and, when the user picked
// the "generate" action, immediately triggers generation for it afterwards. Pass
// useCampaignSaveMutation for create forms or useCampaignUpdateMutation for edit forms.
export const useCampaignSaveAndGenerate = (useSaveMutation: typeof useCampaignSaveMutation) => {
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveIsInProgress }] = useSaveMutation();
    const [generateCampaign, { isLoading: generateIsInProgress }] = useCampaignMutation();

    const saveAndGenerate = useCallback(
        async (data: CampaignEntity, formAction: FormSubmitAction | undefined) => {
            const saveResult = await saveFormData({ data });

            if ("error" in saveResult) {
                return saveResult;
            }

            if (formAction === FORM_ACTION.GENERATE) {
                const groupCode = getAssignedDataGroup()?.code;
                const generateResult = await generateCampaign({
                    url: `${ResourceName.DATA_GROUPS}/${groupCode}/${ResourceName.CAMPAIGNS}/${saveResult.data.id}/${FORM_ACTION.GENERATE}`,
                });

                if ("error" in generateResult) {
                    dispatch(
                        showErrorNotification({
                            text: i18n.t("error.campaign.generate.failed", { 0: saveResult.data.id }),
                            autoCloseDisabled: true,
                        }),
                    );
                }

                return generateResult;
            }

            return saveResult;
        },
        [saveFormData, generateCampaign, dispatch],
    );

    return { saveAndGenerate, isSaveInProgress: saveIsInProgress || generateIsInProgress };
};
